import React, { useState, useEffect } from 'react';
import { ImagePlus, Download, ExternalLink, X } from 'lucide-react';

const PINATA_JWT = import.meta.env.VITE_PINATA_JWT;
const PINATA_GATEWAY = import.meta.env.VITE_PINATA_GATEWAY || 'bronze-careful-meerkat-552.mypinata.cloud';
const PINATA_GROUP_ID = import.meta.env.VITE_PINATA_GROUP_ID || '41ac0866-3a20-44cb-bc7b-804cb845c861';

interface MemeItem {
    id: string;
    cid: string;
    name: string;
    url: string;
}

const getImageUrl = (cid: string) => `https://${PINATA_GATEWAY}/ipfs/${cid}`;

const Memes: React.FC = () => {
    const [memes, setMemes] = useState<MemeItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedMeme, setSelectedMeme] = useState<MemeItem | null>(null);

    useEffect(() => {
        const fetchMemes = async () => {
            if (!PINATA_JWT) {
                setError('Pinata API key not configured');
                setLoading(false);
                return;
            }

            try {
                const res = await fetch(
                    `https://api.pinata.cloud/data/pinList?status=pinned&pageLimit=100&groupId=${PINATA_GROUP_ID}&sortBy=date_pinned&sortOrder=DESC`,
                    { headers: { Authorization: `Bearer ${PINATA_JWT}` } }
                );

                if (!res.ok) throw new Error('Failed to fetch memes');

                const data = await res.json();
                const imageFiles: MemeItem[] = data.rows
                    .filter((f: any) => f.mime_type?.startsWith('image/'))
                    .reverse()
                    .map((f: any, index: number) => ({
                        id: f.id,
                        cid: f.ipfs_pin_hash,
                        name: `DUCK Meme #${index + 1}`,
                        url: getImageUrl(f.ipfs_pin_hash),
                    }));
                setMemes(imageFiles);
            } catch (err) {
                setError('Failed to load memes');
                console.error('Pinata fetch error:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchMemes();
    }, []);

    const formatName = (name: string) => name;

    const skeletonItems = Array(12).fill(null);

    return (
        <section className="memes-section">
            <div className="memes-container">
                <div className="memes-header">
                    <h1 className="memes-title">Meme Gallery</h1>
                    <p className="memes-subtitle">The finest collection of $DUCK memes on Monad</p>
                </div>

                {loading ? (
                    <div className="memes-grid">
                        {skeletonItems.map((_, index) => (
                            <div key={index} className="meme-card skeleton">
                                <div className="meme-image-skeleton">
                                    <ImagePlus size={32} className="skeleton-icon" />
                                </div>
                                <div className="meme-info-skeleton">
                                    <div className="skeleton-line short"></div>
                                    <div className="skeleton-line"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : error ? (
                    <div className="memes-coming-soon">
                        <h2>{error}</h2>
                        <p>Please check configuration and try again.</p>
                    </div>
                ) : memes.length === 0 ? (
                    <div className="memes-coming-soon">
                        <span className="coming-soon-badge">Coming Soon</span>
                        <h2>No Memes Yet!</h2>
                        <p>Memes will appear here once uploaded. Stay tuned!</p>
                    </div>
                ) : (
                    <div className="memes-grid">
                        {memes.map((meme) => (
                            <div
                                key={meme.id}
                                className="meme-card"
                                onClick={() => setSelectedMeme(meme)}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="meme-image-wrapper">
                                    <img
                                        src={meme.url}
                                        alt={meme.name}
                                        loading="lazy"
                                        className="meme-image"
                                        onError={(e) => {
                                            (e.target as HTMLImageElement).style.display = 'none';
                                            const parent = (e.target as HTMLImageElement).parentElement;
                                            if (parent && !parent.querySelector('.meme-error')) {
                                                const errorDiv = document.createElement('div');
                                                errorDiv.className = 'meme-error';
                                                errorDiv.innerHTML = '<span>Failed to load</span>';
                                                parent.appendChild(errorDiv);
                                            }
                                        }}
                                    />
                                </div>
                                <div className="meme-info">
                                    <span className="meme-name">{formatName(meme.name)}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Lightbox Modal */}
            {selectedMeme && (
                <div
                    className="meme-lightbox"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setSelectedMeme(null);
                    }}
                >
                    <div className="meme-lightbox-content">
                        <button
                            className="meme-lightbox-close"
                            onClick={() => setSelectedMeme(null)}
                        >
                            <X size={24} />
                        </button>
                        <img
                            src={selectedMeme.url}
                            alt={selectedMeme.name}
                            className="meme-lightbox-image"
                        />
                        <div className="meme-lightbox-footer">
                            <span className="meme-lightbox-name">
                                {formatName(selectedMeme.name)}
                            </span>
                            <div className="meme-lightbox-actions">
                                <a
                                    href={selectedMeme.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="meme-action-btn"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <ExternalLink size={16} />
                                    Open
                                </a>
                                <a
                                    href={selectedMeme.url}
                                    download={selectedMeme.name}
                                    className="meme-action-btn"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <Download size={16} />
                                    Download
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default Memes;
